/**
 * Switch de actualización automática, para el encabezado de cada pantalla.
 *
 * Dice tres cosas de un vistazo: si la pantalla se está actualizando sola (el
 * switch y un punto que late), cada cuánto ("cada 10 s", o "Pausada") y qué tan
 * fresco es lo que se está mirando ("Actualizado hace 4 s", que se recalcula
 * solo). Va al lado del botón "Actualizar", que sigue sirviendo con el switch
 * apagado.
 *
 * Es un `<button role="switch">`: se activa con clic, Enter o espacio y el lector
 * de pantalla lo anuncia como interruptor con su estado. La línea de estado es
 * su descripción accesible, pero sin `aria-live`: anunciar cada segundo que pasó
 * un segundo sería ruido.
 */
import { useEffect, useId, useState } from 'react';
import type { ReactElement } from 'react';
import { describeInterval, describeLastUpdate } from '../lib/auto-refresh';
import { formatDateTime } from '../lib/format';
import '../styles/auto-refresh.css';

export interface AutoRefreshToggleProps {
  /** Si la actualización automática está encendida. */
  readonly enabled: boolean;
  readonly onChange: (enabled: boolean) => void;
  /** Cadencia que se anuncia mientras está encendida. */
  readonly intervalMs: number;
  /** Cuándo llegó el dato que se está mostrando (epoch ms); `null` si todavía no hay. */
  readonly updatedAt: number | null;
  /** La última consulta falló: el dato en pantalla es el último bueno. */
  readonly failing?: boolean;
  /** Qué se actualiza y cómo, cuando el intervalo solo no lo explica. */
  readonly hint?: string;
}

/** Cada cuánto se recalcula el "Actualizado hace…". */
const CLOCK_TICK_MS = 1000;

type ToggleState = 'on' | 'off' | 'failing';

export function AutoRefreshToggle({
  enabled,
  onChange,
  intervalMs,
  updatedAt,
  failing = false,
  hint,
}: AutoRefreshToggleProps): ReactElement {
  const statusId = useId();
  const state: ToggleState = !enabled ? 'off' : failing ? 'failing' : 'on';

  return (
    <div className="auto-refresh" data-state={state} title={hint}>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-describedby={statusId}
        className="auto-refresh__switch"
        onClick={() => onChange(!enabled)}
      >
        <span className="auto-refresh__track" aria-hidden="true">
          <span className="auto-refresh__thumb" />
        </span>
        <span className="auto-refresh__label">Actualización automática</span>
      </button>
      <span id={statusId} className="auto-refresh__status">
        <span className="auto-refresh__dot" aria-hidden="true" />
        <span>{enabled ? describeInterval(intervalMs) : 'Pausada'}</span>
        {updatedAt === null ? null : (
          <>
            <span aria-hidden="true">·</span>
            <LastUpdate updatedAt={updatedAt} />
          </>
        )}
        {failing ? <span className="visually-hidden">La última actualización falló.</span> : null}
        {hint === undefined ? null : <span className="visually-hidden">{hint}</span>}
      </span>
    </div>
  );
}

/**
 * "Actualizado hace 4 s", recalculado cada segundo.
 *
 * El reloj vive acá y no en la pantalla: el que se vuelve a renderizar cada
 * segundo es este texto, no la tabla ni el grafo.
 */
function LastUpdate({ updatedAt }: { readonly updatedAt: number }): ReactElement {
  const now = useNow(CLOCK_TICK_MS);
  const iso = new Date(updatedAt).toISOString();
  return (
    <time className="auto-refresh__time" dateTime={iso} title={formatDateTime(iso)}>
      {describeLastUpdate(updatedAt, now)}
    </time>
  );
}

function useNow(tickMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), tickMs);
    return () => clearInterval(id);
  }, [tickMs]);
  return now;
}
